import { Download, Calendar, User, Gem } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { PermissionsKeys } from "components/permissions/permission-keys";
import { TruncatedText } from "components/shared/truncated-text";
import { TooltipWrapper, PermissionTooltipWrapper } from "components/permissions/permissions";
import { usePermissions } from "components/permissions/usePermissions";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { openDownloadLinkCallback } from "utils/download";
import { hasPremiumData } from "../../clip-menu";
import { ClipConfiguration } from "../../models";
import { getStatusColor } from "../../../clip-utils";
import { MapThumbnail } from "./map-thumbnail";

interface ClipItemProps {
  clip: ClipConfiguration;
  onOpen: (clip: ClipConfiguration) => void;
  onSelect: (clip: ClipConfiguration) => void;
  isSelected: boolean;
}

export const ClipItem = (props: ClipItemProps) => {
  const { isPermitted, permissionDeniedMessageString, isPermissionsSuccess } =
    usePermissions();

  const clipPermissions = [
    PermissionsKeys.clipCreate,
    PermissionsKeys.clipUpdate,
    PermissionsKeys.clipRead,
  ];

  const geometry =
    props.clip.geometry && props.clip.geometry.features.length > 0
      ? props.clip.geometry
      : undefined;

  const premiumClip = hasPremiumData(props.clip);

  // Allow opening all clips regardless of premium data - subscription check happens at submission
  const isClipPermitted = isPermissionsSuccess && isPermitted(clipPermissions);
  const permissionDeniedString = !isClipPermitted
    ? permissionDeniedMessageString(clipPermissions)
    : "";

  dayjs.extend(utc);

  return (
    <div
      className={`min-h-[100px] flex flex-row gap-0.5 rounded-[15px] border cursor-pointer overflow-hidden ${
        props.isSelected
          ? "bg-primary/10 border-primary"
          : "hover:bg-muted"
      }`}
      onClick={(e) => {
        e.stopPropagation();
        e.preventDefault();
        props.onSelect(props.clip);
      }}
    >
      <div>
        <MapThumbnail geometry={geometry} />
      </div>

      <div className="w-[226px] p-2">
        <div className="h-full flex flex-col justify-between">
          <div className="flex flex-row justify-between items-center">
            <TruncatedText
              as="span"
              className="text-sm font-medium max-w-35"
            >
              {props.clip.name}
            </TruncatedText>
            <Badge
              variant="outline"
              className={getStatusColor(props.clip.status)}
            >
              {props.clip.status}
            </Badge>
          </div>

          <div className="flex flex-row justify-between items-center">
            <div className="flex flex-row items-center gap-2 min-w-0">
              <User className="size-4 text-muted-foreground shrink-0" />
              <TruncatedText
                as="span"
                className="text-xs text-muted-foreground max-w-full"
              >
                {props.clip.authorName || props.clip.developer || ""}
              </TruncatedText>
            </div>
            {premiumClip && <Gem color="#1876d3" size={20} />}
          </div>

          <div className="flex flex-row justify-between items-center">
            <div className="flex flex-row items-center gap-2">
              <Calendar className="size-4 text-muted-foreground" />
              <span className="text-xs text-muted-foreground">
                {dayjs.utc(props.clip.date).local().format("YYYY-MM-DD")}
              </span>
            </div>
            <div className="flex items-center gap-1">
              {props.clip.status === "Completed" && premiumClip && (
                <PermissionTooltipWrapper
                  permissionKeys={[PermissionsKeys.premiumDataRead]}
                >
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    onClick={openDownloadLinkCallback(
                      `${import.meta.env.VITE_DELIVERY_API_URL}/clip/${
                        props.clip.id
                      }/output`
                    )}
                  >
                    <Download className="size-4 text-primary" />
                  </Button>
                </PermissionTooltipWrapper>
              )}
              {props.clip.status === "Completed" && !premiumClip && (
                <Button
                  variant="ghost"
                  size="icon-sm"
                  onClick={openDownloadLinkCallback(
                    `${import.meta.env.VITE_DELIVERY_API_URL}/clip/${
                      props.clip.id
                    }/output`
                  )}
                >
                  <Download className="size-4 text-primary" />
                </Button>
              )}
              <TooltipWrapper title={permissionDeniedString}>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => props.onOpen(props.clip)}
                  disabled={!isClipPermitted}
                  className="px-1.5 py-0.5 h-auto min-w-0"
                >
                  Open
                </Button>
              </TooltipWrapper>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
